import { useState } from 'react';
import Sheet from './Sheet.js';
import { listTasks, setFocus } from './api.js';
import { taskCode } from './format.js';
import { useLoad } from './useLoad.js';
import { errorText, useToast } from './toast.js';
import { Check, DueChip, Loader } from './ui.js';
import type { TaskRow } from './types.js';

export const FOCUS_MAX = 3;

/**
 * "Pick today's 3" (spec §6): choose up to three actionable tasks to pin to
 * today. Saving replaces today's focus; Undo restores the previous picks.
 */
export default function FocusPicker({
  today,
  current,
  onClose,
  onSaved,
}: {
  today: string;
  /** Tasks focused today before opening. */
  current: readonly TaskRow[];
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const toast = useToast();
  const tasks = useLoad(() => listTasks({ view: 'now', limit: 30 }), []);
  const before = current.filter((t) => t.focusOn === today).map((t) => t.id);
  const [picked, setPicked] = useState<string[]>(before);
  const [saving, setSaving] = useState(false);

  const toggle = (id: string): void =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= FOCUS_MAX ? p : [...p, id]));

  async function save(): Promise<void> {
    setSaving(true);
    try {
      await setFocus(picked);
      toast.undoable({
        text: picked.length ? `Today's ${picked.length === 1 ? 'one' : picked.length} set` : 'Focus cleared',
        undo: () => setFocus(before),
        onUndone: onSaved,
      });
      onSaved();
      onClose();
    } catch (err) {
      toast.error(`Couldn't set focus: ${errorText(err)}`);
      setSaving(false);
    }
  }

  return (
    <Sheet title="Pick today's 3" onClose={onClose}>
      <p className="lo-sheet__hint">
        <span className="lo-num">
          {picked.length}/{FOCUS_MAX}
        </span>{' '}
        picked
      </p>
      <Loader state={tasks}>
        {(d) => (
          <ul className="lo-pick">
            {d.items.map((t) => {
              const on = picked.includes(t.id);
              return (
                <li key={t.id} className={`lo-pick__row${on ? ' lo-pick__row--on' : ''}`}>
                  <Check checked={on} onChange={() => toggle(t.id)} label={`Pick ${taskCode(t.shortCode)} ${t.title}`} />
                  <span className="lo-pick__title">{t.title}</span>
                  <span className="lo-code">{taskCode(t.shortCode)}</span>
                  <DueChip dueOn={t.dueOn} today={today} />
                </li>
              );
            })}
          </ul>
        )}
      </Loader>
      <div className="lo-form__actions">
        <button type="button" className="btn" disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Set focus'}
        </button>
      </div>
    </Sheet>
  );
}
